import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { FieldSetApi, FieldSetField, FieldInput, SubmitButton, SubmitEvent, SubmitFunction, MAIN_SUBMIT_BUTTON_NAME } from "../shared/types";
import { binaryInsertion, compareElementPositionInDOM, DebugLog } from "../shared/utils";

export interface UseFieldSetProps {
    name?: string,
    onSubmit?: SubmitFunction,
    disabled?: boolean,

    parentFieldSet?: FieldSetApi,
    disableWithParent?: boolean
}

export default function useFieldSet({
    name: receivedName,
    disabled: receivedDisabled,
    onSubmit,
    parentFieldSet,
    disableWithParent = true
}: UseFieldSetProps): FieldSetApi {
    const fieldSetId = (`${receivedName || "field-set"}-`) + useId();
    const fieldSetName = receivedName || fieldSetId;
    const fieldSetDisabled = Boolean(receivedDisabled || (disableWithParent && parentFieldSet?.disabled));

    const fieldSetRef = useRef<HTMLElement>(null!);
    const fieldList = useRef<(FieldInput | FieldSetField | SubmitButton)[]>([]).current;
    const findFieldIndexById = useCallback((id: string) => fieldList.findIndex(f => f.id === id), []);
    const findFieldById = useCallback((id: string) => fieldList.find(f => f.id == id), []);
    const findFieldByName = useCallback((name: string) => fieldList.find(f => f.name == name), []);
    const findSubmitButtonById = useCallback((id: string) => fieldList.find(f => f.id === id && f.type === "submitButton") as SubmitButton | undefined, []);
    const findSubmitButtonByName = useCallback((name: string) => fieldList.find(f => f.name === name && f.type === "submitButton") as SubmitButton | undefined, []);

    //Register / Unregister
    const register = useCallback((field: FieldInput | FieldSetField | SubmitButton) => {
        binaryInsertion(fieldList, field,
            (f1, f2) => compareElementPositionInDOM(f1.element, f2.element, fieldSetRef.current || undefined));
        DebugLog(`Registered field ${field.id}.`, fieldList);
    }, [])

    const unregister = useCallback((id: string) => {
        const index = findFieldIndexById(id);
        if (index == -1) return;
        fieldList.splice(index, 1);
        DebugLog(`Registered field ${id}.`, fieldList);
    }, []);

    //Focus Management
    const focusFieldById = useCallback((id: string, select?: boolean) => {
        const field = findFieldById(id);
        if (!field) return false;
        if (field.type === "fieldSetField") return field.focusFirst(select);
        else if (field.type == "fieldInput") return field.focus(select);
        return false;
    }, []);

    const focusFieldByName = useCallback((name: string, select?: boolean) => {
        const field = findFieldByName(name);
        if (!field) return false;
        if (field.type === "fieldSetField") return field.focusFirst(select);
        else if (field.type == "fieldInput") return field.focus(select);
        return false;
    }, []);

    const focusNextField = useCallback((currentId: string, select?: boolean) => {
        const currentIndex = findFieldIndexById(currentId);
        if (currentIndex === -1) return false;

        for (let i = currentIndex + 1; i < fieldList.length; i++) {
            const nextField = fieldList[i];
            if (nextField.type == "fieldInput") {
                if (nextField.focus(select)) return true;
            } else if (nextField.type == "fieldSetField") {
                if (nextField.focusFirst(select)) return true;
            }
        }

        clickSubmitButton();

        return false;
    }, [])

    const focusPreviousField = useCallback((currentId: string, select?: boolean) => {
        const currentIndex = findFieldIndexById(currentId);
        if (currentIndex === -1) return false;

        for (let i = currentIndex - 1; i >= 0; i--) {
            const nextField = fieldList[i];
            if (nextField.type == "fieldInput") {
                if (nextField.focus(select)) return true;
            } else if (nextField.type == "fieldSetField") {
                if (nextField.focusLast(select)) return true;
            }
        }

        if (parentFieldSet)
            return parentFieldSet.focusPreviousField(fieldSetId);
        return false;
    }, [])

    const focusFirstField = useCallback((select?: boolean) => {
        for (let i = 0; i < fieldList.length; i++) {
            const field = fieldList[i];
            if (field.type == "fieldInput") {
                if (field.focus(select)) return true;
            } else if (field.type == "fieldSetField") {
                if (field.focusFirst(select)) return true;
            }
        }
        return false;
    }, [])

    const focusLastField = useCallback((select?: boolean) => {
        for (let i = fieldList.length - 1; i >= 0; i--) {
            const field = fieldList[i];
            if (field.type == "fieldInput") {
                if (field.focus(select)) return true;
            } else if (field.type == "fieldSetField") {
                if (field.focusLast(select)) return true;
            }
        }
        return false;
    }, [])

    //Submit Buttons
    const [submitterId, setSubmitterId] = useState<string | undefined>(undefined);

    const focusSubmitButtonById = useCallback((id: string) => {
        const submitButton = findSubmitButtonById(id);
        if (!submitButton) return false;
        return submitButton.focus();
    }, [])

    const focusSubmitButtonByName = useCallback((name: string) => {
        const submitButton = findSubmitButtonByName(name);
        if (!submitButton) return false;
        return submitButton.focus();
    }, [])

    const clickSubmitButton = useCallback((id?: string) => {
        const submitButton = id ? findSubmitButtonById(id) : findSubmitButtonByName(MAIN_SUBMIT_BUTTON_NAME);
        if (!submitButton || !submitButton.element) return;
        if ("disabled" in submitButton.element && submitButton.element.disabled) return;
        submitButton.element.click();
    }, [])

    const submit = useCallback((id?: string) => {
        if (!onSubmit) return;
        const submitButton = id ? findSubmitButtonById(id) : findSubmitButtonByName(MAIN_SUBMIT_BUTTON_NAME);
        if (!submitButton) return;

        const event = new SubmitEvent("submit", { submitter: submitButton.name, bubbles: false, cancelable: true, composed: false })
        const result = onSubmit(event);

        if (result instanceof Promise) {
            setSubmitterId(submitButton.id);
            return result.finally(() => setSubmitterId(undefined));
        }
    }, []);

    //Register to parent fieldset
    useEffect(() => {
        if (!parentFieldSet) return;

        parentFieldSet.register({
            id: fieldSetId,
            name: fieldSetName,
            element: fieldSetRef.current,
            type: "fieldSetField",
            focusField: focusFieldById,
            focusFirst: focusFirstField,
            focusLast: focusLastField,
        });

        return () => parentFieldSet.unregister(fieldSetId);
    }, [fieldSetId, fieldSetName, focusFieldById, focusFirstField, focusLastField]);

    //Return Field Set Api
    return useMemo(() => ({
        id: fieldSetId,
        name: fieldSetName,
        ref: fieldSetRef,
        disabled: fieldSetDisabled,
        register,
        unregister,
        focusFieldById,
        focusFieldByName,
        focusFirstField,
        focusLastField,
        focusNextField,
        focusPreviousField,
        focusSubmitButtonById,
        focusSubmitButtonByName,
        clickSubmitButton,
        submit,
        submitterId,
        parentFieldSet
    }), [fieldSetId, fieldSetName, fieldSetDisabled, submitterId, parentFieldSet])
}